import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { MetricFilterSource } from 'app/hub-components/models/metric-filter-source.model';
import { Dimension, Metric, Schema } from '../../models/analytics-api/schema.model';
import { QueryFilter } from '../../models/query-filter.model';
import { QueryBuilder } from '../../modules/query-builder/query-builder';
import { Query } from '../../models/analytics-api/query.model';
import { FilterSource } from 'app/hub-components/models/filter-source.model';
import { MetricFilter } from 'app/hub-components/models/metric-filter.model';

@Injectable()
export class QueryService {
  private dimensionsSubject: BehaviorSubject<any[]> = new BehaviorSubject<any[]>(undefined);
  private metricsSubject: BehaviorSubject<any[]> = new BehaviorSubject<any[]>(undefined);
  private runQuerySubject: Subject<void> = new Subject<void>();
  private queryFiltersSubject: BehaviorSubject<any[]> = new BehaviorSubject<any[]>(undefined);
  private metricFiltersSubject: BehaviorSubject<any[]> = new BehaviorSubject<any[]>(undefined);
  private queryFiltersDatabase = [];
  private _queryBuilderMap: Map<string | number, QueryBuilder> = new Map<string | number, QueryBuilder>();

  constructor() {}

  public fetchQueryBuilder(queryId: string | number, query: Query): QueryBuilder {
    const queryBuilder = new QueryBuilder(query);
    this._queryBuilderMap.set(queryId, queryBuilder);
    return queryBuilder;
  }

  public hasQueryBuilder(queryId: string | number): boolean {
    return this._queryBuilderMap.has(queryId);
  }

  public getIncompatibleFilterSources(filterSources: FilterSource[], schema?: Schema): string[] {
    const dimsAndProps = filterSources.map(source => source.dimension || source.dimensionProperty);
    const compatibleDimensions = schema.compatibleDimensions(dimsAndProps);
    const incompatibleDimensionsList = schema.dimensions.filter(dimension => !compatibleDimensions.includes(dimension));
    const incompatibleDimensionsNames = incompatibleDimensionsList.map(dim => dim && dim.name);
    return incompatibleDimensionsNames;
  }

  public updateEditableDimensions(filterSources: FilterSource[], schema?: Schema) {
    const dimsAndProps = filterSources.map(source => source.dimension || source.dimensionProperty);
    const compatibleDimensions = schema ? schema.compatibleDimensions(dimsAndProps) : [];
    const requiredDimensions = schema ? schema.requiredDimensions() : [];

    requiredDimensions.forEach(x => compatibleDimensions.remove(x));

    this.dimensionsSubject.next(compatibleDimensions.sortBy(dim => dim.displayName));
  }

  public getFilterSourceList(queryDimensions: Dimension[] = []): FilterSource[] {
    const filterSourceDims = queryDimensions.map(dim => new FilterSource(dim));
    const filterSourceProperties = queryDimensions
      .flatMap(dim => dim.properties.filter(property => !!property))
      .map((prop, i) => {
        const selDim = queryDimensions.find(dim => dim.name === prop.dimensionName);
        return new FilterSource(selDim, prop, i === 0 ? 'Attributes' : null);
      });

    return [...filterSourceDims, ...filterSourceProperties];
  }

  public getMetricFilterSourceList(queryMetrics: Metric[] = []): MetricFilterSource[] {
    return queryMetrics.map(metric => new MetricFilterSource(metric));
  }

  public getEditableDimensions(): Observable<any[]> {
    return this.dimensionsSubject.asObservable();
  }

  public getQueryFilters(): Observable<any[]> {
    return this.queryFiltersSubject.asObservable();
  }

  public updateQueryFilters(data) {
    this.queryFiltersSubject.next(data);
  }

  public get queryFilters() {
    return this.queryFiltersSubject.getValue();
  }

  public getMetricFilters(): Observable<any[]> {
    return this.metricFiltersSubject.asObservable();
  }

  public updateMetricFilters(data) {
    this.metricFiltersSubject.next(data);
  }

  public get metricFilters() {
    return this.metricFiltersSubject.getValue();
  }

  public getRunQueryStatus(): Observable<void> {
    return this.runQuerySubject.asObservable();
  }

  public executeRunQuery() {
    this.runQuerySubject.next();
  }

  public cacheQueryFilters(data: any[]) {
    this.queryFiltersDatabase = data;
  }

  public get cachedQueryFilters(): any[] {
    return this.queryFiltersDatabase;
  }

  public findFilterSource(filter: QueryFilter, filterSources: FilterSource[]) {
    return filterSources.find(filterSource => {
      if (!!filter.dimensionProperty?.name) {
        return filterSource.dimensionName === filter.dimension.name && filterSource.name === filter.dimensionProperty.name;
      } else if (filter.dimension) {
        return filterSource.name === filter.dimension.name;
      } else {
        if (filterSource.key === filter.key) {
          return true;
        } else if (!filter.key.includes('.')) {
          return filterSource.key === `${filter.key}.${filter.key}`;
        } else if (!filterSource.isDimensionProperty) {
          const useAsDimensionHasBeenToggledOn = filterSource.key === this.transformToUseAsDimensionName(filter.key);
          return useAsDimensionHasBeenToggledOn;
        } else if (filterSource.isDimensionProperty) {
          const useAsDimensionHasBeenToggledOff = filter.key === this.transformToUseAsDimensionName(filterSource.key);
          return useAsDimensionHasBeenToggledOff;
        } else {
          return false;
        }
      }
    });
  }

  private transformToUseAsDimensionName(key: string) {
    return `${key.replace('.', '_')}.${key.replace('.', '_')}`;
  }

  public findMetricFilterSource(filter: MetricFilter, filterSources: MetricFilterSource[]) {
    return filterSources.find(filterSource => {
      if (filter.metric) {
        return filterSource.name === filter.metric.name;
      } else {
        return filterSource.key === filter.key;
      }
    });
  }

  /**
   * Duplicated in query.model for now.
   * Will later remove and only use this one when refactor starts
   */

   public processFilters(filters) {
    return filters.map(filter => {
      return { expressions: filter.expressions.map(expression => new QueryFilter(expression)) }
    });
  }

  public processMetricFilters(filters) {
    return filters.map(filter => {
      return { expressions: filter.expressions.map(expression => new MetricFilter(expression)) };
    });
  }

  public formatFilters(filters) {
    return filters.map(filter => {
      return { expressions: filter.expressions.map(expression => expression.asJson()) }
    });
  }

}
